import { useMemo, useRef } from "react"

import {
  useAutoDismissTimers,
  useDismissHandlerFactory,
  useFocusManager,
  useMountedLifecycle,
  useRegisterToastRef
} from "./hooks"
import ToastContext from "./ToastContext"
import { renderToastLayer } from "./ToastLayer"
import type {
  ToastContextValue,
  ToastPlacement,
  ToastProviderProps
} from "./types"
import useToastStore from "./useToastStore"
import { usePrefersReducedMotion } from "./utils"

const DEFAULT_MAX_VISIBLE = 3
const DEFAULT_GAP = 8
const DEFAULT_STAGGER = 40
const DEFAULT_AUTO_DISMISS_DELAY = 5000
const DEFAULT_DESKTOP_PLACEMENT: ToastPlacement = "bottom-right"
const DEFAULT_TABLET_PLACEMENT: ToastPlacement = "bottom-right"
const DEFAULT_MOBILE_PLACEMENT: ToastPlacement = "top-center"

const useToastRefs = () => {
  const exitTimersRef = useRef<Map<string, number>>(new Map())
  const autoDismissTimersRef = useRef<Map<string, number>>(new Map())
  const focusQueueRef = useRef<Set<string>>(new Set())
  const toastRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())

  return {
    exitTimersRef,
    autoDismissTimersRef,
    focusQueueRef,
    toastRefs
  }
}

const useToastEffects = ({
  visibleToasts,
  defaultAutoDismissDelay,
  dismissToast,
  autoDismissTimersRef,
  focusQueueRef,
  toastRefs
}: {
  visibleToasts: ReturnType<typeof useToastStore>["visibleToasts"]
  defaultAutoDismissDelay: number
  dismissToast: ReturnType<typeof useToastStore>["dismissToast"]
  autoDismissTimersRef: ReturnType<typeof useToastRefs>["autoDismissTimersRef"]
  focusQueueRef: ReturnType<typeof useToastRefs>["focusQueueRef"]
  toastRefs: ReturnType<typeof useToastRefs>["toastRefs"]
}) => {
  useAutoDismissTimers({
    visibleToasts,
    defaultAutoDismissDelay,
    dismissToast,
    autoDismissTimersRef
  })

  useFocusManager({
    visibleToasts,
    focusQueueRef,
    toastRefs
  })
}

const ToastProvider = ({
  children,
  maxVisible = DEFAULT_MAX_VISIBLE,
  defaultAutoDismissDelay = DEFAULT_AUTO_DISMISS_DELAY,
  gap = DEFAULT_GAP,
  staggerMs = DEFAULT_STAGGER,
  placement,
  desktopPlacement,
  tabletPlacement,
  mobilePlacement,
  solidBackground = false,
  toneColors
}: ToastProviderProps) => {
  const prefersReducedMotion = usePrefersReducedMotion()

  const { exitTimersRef, autoDismissTimersRef, focusQueueRef, toastRefs } =
    useToastRefs()

  const placements = useMemo(
    () => ({
      desktop: desktopPlacement ?? placement ?? DEFAULT_DESKTOP_PLACEMENT,
      tablet: tabletPlacement ?? placement ?? DEFAULT_TABLET_PLACEMENT,
      mobile: mobilePlacement ?? placement ?? DEFAULT_MOBILE_PLACEMENT
    }),
    [desktopPlacement, mobilePlacement, placement, tabletPlacement]
  )

  const { visibleToasts, dismissToast, dismissAll, pushToast, updateToast } =
    useToastStore({
      maxVisible,
      exitTimersRef,
      autoDismissTimersRef,
      toastRefs,
      focusQueueRef
    })

  const mounted = useMountedLifecycle(
    exitTimersRef,
    autoDismissTimersRef,
    focusQueueRef
  )

  useToastEffects({
    visibleToasts,
    defaultAutoDismissDelay,
    dismissToast,
    focusQueueRef,
    toastRefs,
    autoDismissTimersRef
  })

  const registerToastRef = useRegisterToastRef(toastRefs)
  const getManualDismiss = useDismissHandlerFactory(dismissToast)

  const contextValue: ToastContextValue = useMemo(
    () => ({
      pushToast,
      updateToast,
      dismissToast: (id: string) => dismissToast(id, "manual"),
      dismissAll
    }),
    [dismissAll, dismissToast, pushToast, updateToast]
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {mounted
        ? renderToastLayer({
            visibleToasts,
            gap,
            prefersReducedMotion,
            defaultAutoDismissDelay,
            staggerMs,
            dismissAll,
            registerToastRef,
            getDismissHandler: getManualDismiss,
            desktopPlacement: placements.desktop,
            tabletPlacement: placements.tablet,
            mobilePlacement: placements.mobile,
            solidBackground,
            toneColors
          })
        : null}
    </ToastContext.Provider>
  )
}

export default ToastProvider
